/**
 * Gestión de estudiantes (solo Administración).
 *
 * El alta tiene una particularidad que la interfaz debe hacer evidente: un
 * estudiante que entra a mitad de ciclo NO nace sin deudas — hereda todos los
 * cargos ya generados, con sus fechas originales y la mora que corresponda. Por eso
 * antes de guardar se muestra exactamente qué va a heredar y cuánto suma.
 *
 * Requiere conexión: el código es único en toda la institución y esa unicidad solo
 * puede confirmarla el servidor.
 */

import { useCallback, useEffect, useState } from "react";
import type {
  InheritedChargesPreviewDto,
  StudentAdminDto,
} from "@senm/contracts";
import { formatMoney, money, newId } from "@senm/domain";
import { ApiRequestError, apiRequest } from "../api/client.js";
import type { StoredSession } from "../local/session.js";
import {
  CodigoDuplicadoLocalError,
  crearEstudianteLocal,
} from "../services/students.js";

type Filtro = "ACTIVOS" | "INACTIVOS" | "TODOS";

interface Ciclo {
  id: string;
  name: string;
  closed: boolean;
  isCurrent: boolean;
}

interface Grado {
  id: string;
  name: string;
  sections: { id: string; name: string }[];
}

/** Campos del formulario. Se mantienen como texto hasta enviar. */
interface Formulario {
  code: string;
  firstNames: string;
  lastNames: string;
  birthDate: string;
  guardianName: string;
  guardianPhone: string;
  gradeId: string;
  sectionId: string;
}

const FORMULARIO_VACIO: Formulario = {
  code: "",
  firstNames: "",
  lastNames: "",
  birthDate: "",
  guardianName: "",
  guardianPhone: "",
  gradeId: "",
  sectionId: "",
};

export function StudentsAdminPage(props: {
  session: StoredSession;
  conexion: string;
  /**
   * Sincroniza el dispositivo. Se llama tras cada alta, edición o baja.
   *
   * Esta pantalla habla con el SERVIDOR; la de Estudiantes, la que usa caja, lee
   * lo guardado en el dispositivo. Sin esto, un alumno recién inscrito aparecía
   * aquí y no existía para cobrarle, y el indicador de arriba decía «Todo está
   * sincronizado» —que solo significa que no hay nada pendiente de ENVIAR—, así
   * que nada delataba que faltaba traerlo.
   */
  onCambio: () => Promise<void>;
}): JSX.Element {
  const [estudiantes, setEstudiantes] = useState<StudentAdminDto[]>([]);
  const [filtro, setFiltro] = useState<Filtro>("ACTIVOS");
  const [busqueda, setBusqueda] = useState("");
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const [ciclos, setCiclos] = useState<Ciclo[]>([]);
  const [grados, setGrados] = useState<Grado[]>([]);
  const [cicloElegido, setCicloElegido] = useState("");

  const [formulario, setFormulario] = useState<Formulario>(FORMULARIO_VACIO);
  const [mostrarAlta, setMostrarAlta] = useState(false);
  const [heredados, setHeredados] = useState<InheritedChargesPreviewDto | null>(
    null,
  );
  const [guardando, setGuardando] = useState(false);

  /** Estudiante en edición, o `null` si no hay ninguno abierto. */
  const [editando, setEditando] = useState<StudentAdminDto | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const parametros = new URLSearchParams({ estado: filtro });
      if (busqueda.trim()) parametros.set("search", busqueda.trim());
      const datos = await apiRequest<{ students: StudentAdminDto[] }>(
        `/v1/admin/students?${parametros}`,
      );
      setEstudiantes(datos.students);
    } catch (problema) {
      setError(
        problema instanceof ApiRequestError && problema.isNetworkFailure
          ? "La gestión de estudiantes necesita conexión: el código debe ser único en toda la institución."
          : "No fue posible cargar los estudiantes.",
      );
    } finally {
      setCargando(false);
    }
  }, [filtro, busqueda]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  // Catálogos: alimentan el selector de ciclo y los de grado/sección.
  useEffect(() => {
    void (async () => {
      try {
        const [datosCiclos, datosGrados] = await Promise.all([
          apiRequest<{ cycles: Ciclo[] }>("/v1/cycles"),
          apiRequest<{ grades: Grado[] }>("/v1/grades"),
        ]);
        setCiclos(datosCiclos.cycles);
        setGrados(datosGrados.grades);
        setCicloElegido(
          (previo) =>
            previo ||
            datosCiclos.cycles.find((ciclo) => ciclo.isCurrent && !ciclo.closed)
              ?.id ||
            datosCiclos.cycles.find((ciclo) => !ciclo.closed)?.id ||
            "",
        );
      } catch {
        // Sin catálogos el formulario se deshabilita solo.
      }
    })();
  }, []);

  const mensaje = (problema: unknown, respaldo: string): string =>
    problema instanceof ApiRequestError ? problema.message : respaldo;

  const abrirAlta = (): void => {
    setMostrarAlta(true);
    setFormulario(FORMULARIO_VACIO);
    setError(null);
    setHeredados(null);
  };

  /*
   * Qué cargos heredará, recalculado cada vez que cambia el grado.
   *
   * Depende del grado porque la herencia depende del grado: un colegio con una
   * colegiatura por nivel tiene un lote por banda, y sin el grado la vista previa
   * enseñaba los tres. Se pide al abrir el alta y en cada cambio de grado, no en
   * cada tecla: las dependencias son exactamente esas dos.
   */
  const gradoElegido = formulario.gradeId;
  useEffect(() => {
    if (!mostrarAlta || cicloElegido === "") return;
    let vigente = true;
    void (async () => {
      try {
        const previa = await apiRequest<InheritedChargesPreviewDto>(
          `/v1/admin/cycles/${cicloElegido}/inherited-charges` +
            (gradoElegido === ""
              ? ""
              : `?gradeId=${encodeURIComponent(gradoElegido)}`),
        );
        if (vigente) setHeredados(previa);
      } catch (problema) {
        if (vigente) {
          setError(
            mensaje(
              problema,
              "No fue posible calcular los cargos que heredaría.",
            ),
          );
        }
      }
    })();
    // Si se cambia de grado antes de que llegue la anterior, la que llega tarde
    // no puede pisar a la nueva.
    return () => {
      vigente = false;
    };
  }, [mostrarAlta, cicloElegido, gradoElegido]);

  const crear = async (evento: React.FormEvent): Promise<void> => {
    evento.preventDefault();
    if (guardando) return;
    setGuardando(true);
    setError(null);
    try {
      const cuerpo: Record<string, unknown> = {
        // El identificador se genera aquí, antes de guardar: es lo que hace el alta
        // idempotente si la petición se reintenta.
        studentId: newId(),
        code: formulario.code,
        firstNames: formulario.firstNames,
        lastNames: formulario.lastNames,
        birthDate: formulario.birthDate,
        guardianName: formulario.guardianName,
        guardianPhone: formulario.guardianPhone,
        cycleId: cicloElegido,
      };
      if (formulario.gradeId !== "" && formulario.sectionId !== "") {
        cuerpo.assignment = {
          cycleId: cicloElegido,
          gradeId: formulario.gradeId,
          sectionId: formulario.sectionId,
        };
      }

      if (props.conexion !== "EN_LINEA") {
        // Sin conexión el alta se guarda en el dispositivo y se encola. El código se
        // valida contra lo que hay sincronizado localmente; la unicidad definitiva
        // la confirma el servidor, que abrirá un conflicto si otra caja lo usó.
        const grado = grados.find((g) => g.id === formulario.gradeId);
        await crearEstudianteLocal({
          datos: {
            code: formulario.code,
            firstNames: formulario.firstNames,
            lastNames: formulario.lastNames,
            birthDate: formulario.birthDate,
            guardianName: formulario.guardianName,
            guardianPhone: formulario.guardianPhone,
            cycleId: cicloElegido,
            gradeId: formulario.gradeId || null,
            sectionId: formulario.sectionId || null,
            gradeName: grado?.name ?? null,
            sectionName:
              grado?.sections.find((s) => s.id === formulario.sectionId)
                ?.name ?? null,
          },
          session: props.session,
          now: new Date().toISOString(),
        });
        setAviso(
          `Estudiante ${formulario.code.trim()} guardado en este dispositivo. Se enviará al ` +
            "recuperar la conexión, y entonces recibirá los cargos del ciclo.",
        );
        setMostrarAlta(false);
        setFormulario(FORMULARIO_VACIO);
        return;
      }

      const resultado = await apiRequest<{
        student: StudentAdminDto;
        inheritedCharges: number;
        inheritedCapitalMinor: number;
        currency: string;
      }>("/v1/admin/students", { method: "POST", body: cuerpo });

      setAviso(
        resultado.inheritedCharges === 0
          ? `Estudiante ${resultado.student.code} creado. El ciclo aún no tiene cargos generados.`
          : `Estudiante ${resultado.student.code} creado con ${resultado.inheritedCharges} cargo(s) ` +
              `heredado(s) por ${formatMoney(money(resultado.inheritedCapitalMinor, resultado.currency))}.`,
      );
      setMostrarAlta(false);
      setFormulario(FORMULARIO_VACIO);
      await cargar();
      // El dispositivo tiene que enterarse: caja cobra sobre los datos locales.
      await props.onCambio();
    } catch (problema) {
      // El choque de código detectado localmente trae su propio mensaje, ya redactado
      // para el usuario.
      setError(
        problema instanceof CodigoDuplicadoLocalError
          ? problema.message
          : mensaje(problema, "No fue posible crear el estudiante."),
      );
    } finally {
      setGuardando(false);
    }
  };

  const guardarEdicion = async (evento: React.FormEvent): Promise<void> => {
    evento.preventDefault();
    if (editando === null || guardando) return;
    setGuardando(true);
    setError(null);
    try {
      await apiRequest<StudentAdminDto>(`/v1/admin/students/${editando.id}`, {
        method: "PATCH",
        body: {
          code: formulario.code,
          firstNames: formulario.firstNames,
          lastNames: formulario.lastNames,
          birthDate: formulario.birthDate,
          guardianName: formulario.guardianName,
          guardianPhone: formulario.guardianPhone,
          expectedVersion: editando.version,
        },
      });

      // La asignación va en su propio endpoint porque mover de aula no es editar un
      // dato: cierra la asignación anterior como HISTÓRICA y abre una nueva, y de
      // ella cuelgan las mensualidades del ciclo.
      const vigente = asignacionVigente(editando);
      const cambioAula =
        formulario.gradeId !== "" &&
        formulario.sectionId !== "" &&
        (formulario.gradeId !== (vigente?.gradeId ?? "") ||
          formulario.sectionId !== (vigente?.sectionId ?? ""));

      if (cambioAula) {
        // Con la versión que devuelve el PATCH, no la que teníamos al abrir: el
        // propio guardado acaba de incrementarla y reutilizar la vieja chocaría.
        await apiRequest(`/v1/admin/students/${editando.id}/assignment`, {
          method: "POST",
          body: {
            cycleId: vigente?.cycleId ?? cicloElegido,
            gradeId: formulario.gradeId,
            sectionId: formulario.sectionId,
            // La versión de LA ASIGNACIÓN, no la del estudiante: es lo que compara
            // el servidor. Con 0 se saltaría la comprobación, y dos personas
            // moviendo al mismo alumno a la vez se pisarían sin enterarse.
            expectedVersion: vigente?.version ?? 0,
          },
        });
      }
      setAviso(
        `Datos de ${formulario.firstNames} ${formulario.lastNames} actualizados.`,
      );
      setEditando(null);
      await cargar();
      // El dispositivo tiene que enterarse: caja cobra sobre los datos locales.
      await props.onCambio();
    } catch (problema) {
      setError(mensaje(problema, "No fue posible guardar los cambios."));
    } finally {
      setGuardando(false);
    }
  };

  const cambiarEstado = async (estudiante: StudentAdminDto): Promise<void> => {
    const activar = !estudiante.active;
    if (!activar) {
      const confirmado = window.confirm(
        `¿Dar de baja a ${estudiante.fullName}?\n\n` +
          "Conservará sus cargos, mora, pagos y recibos, y podrá seguir pagando deudas " +
          "anteriores. Solo dejará de recibir cargos nuevos.",
      );
      if (!confirmado) return;
    }
    setError(null);
    try {
      await apiRequest(`/v1/admin/students/${estudiante.id}/active`, {
        method: "POST",
        body: { active: activar, expectedVersion: estudiante.version },
      });
      setAviso(
        activar
          ? `${estudiante.fullName} fue reactivado.`
          : `${estudiante.fullName} fue dado de baja. Sus deudas y pagos se conservan.`,
      );
      await cargar();
      // El dispositivo tiene que enterarse: caja cobra sobre los datos locales.
      await props.onCambio();
    } catch (problema) {
      setError(mensaje(problema, "No fue posible cambiar el estado."));
    }
  };

  /** Asignación vigente del estudiante, que es la que se puede cambiar. */
  const asignacionVigente = (
    estudiante: StudentAdminDto,
  ): StudentAdminDto["assignments"][number] | undefined =>
    estudiante.assignments.find(
      (asignacion) => asignacion.status === "VIGENTE",
    );

  const abrirEdicion = (estudiante: StudentAdminDto): void => {
    setEditando(estudiante);
    setMostrarAlta(false);
    setError(null);
    const vigente = asignacionVigente(estudiante);
    setFormulario({
      code: estudiante.code,
      firstNames: estudiante.firstNames ?? "",
      lastNames: estudiante.lastNames ?? "",
      birthDate: estudiante.birthDate ?? "",
      guardianName: estudiante.guardianName ?? "",
      guardianPhone: estudiante.guardianPhone ?? "",
      // Se precargan para que el formulario muestre dónde está HOY. Abrirlo en
      // blanco invitaría a reasignar sin querer al guardar cualquier otro dato.
      gradeId: vigente?.gradeId ?? "",
      sectionId: vigente?.sectionId ?? "",
    });
  };

  const secciones =
    grados.find((grado) => grado.id === formulario.gradeId)?.sections ?? [];

  const campos = (conAsignacion: boolean): JSX.Element => (
    <div className="campos">
      <label>
        Código del estudiante
        <input
          type="text"
          value={formulario.code}
          onChange={(e) =>
            setFormulario({ ...formulario, code: e.target.value })
          }
          required
          maxLength={30}
          placeholder="EST-0041"
        />
      </label>
      <label>
        Nombres
        <input
          type="text"
          value={formulario.firstNames}
          onChange={(e) =>
            setFormulario({ ...formulario, firstNames: e.target.value })
          }
          required
          minLength={2}
          maxLength={80}
        />
      </label>
      <label>
        Apellidos
        <input
          type="text"
          value={formulario.lastNames}
          onChange={(e) =>
            setFormulario({ ...formulario, lastNames: e.target.value })
          }
          required
          minLength={2}
          maxLength={80}
        />
      </label>
      <label>
        Fecha de nacimiento
        <input
          type="date"
          value={formulario.birthDate}
          onChange={(e) =>
            setFormulario({ ...formulario, birthDate: e.target.value })
          }
          required
        />
      </label>
      <label>
        Responsable
        <input
          type="text"
          value={formulario.guardianName}
          onChange={(e) =>
            setFormulario({ ...formulario, guardianName: e.target.value })
          }
          required
          minLength={3}
          maxLength={120}
        />
      </label>
      <label>
        Teléfono del responsable
        <input
          type="tel"
          value={formulario.guardianPhone}
          onChange={(e) =>
            setFormulario({ ...formulario, guardianPhone: e.target.value })
          }
          required
          placeholder="+502 5555-1234"
        />
      </label>

      {conAsignacion && (
        <>
          <label>
            Grado <span className="ayuda">(opcional)</span>
            <select
              value={formulario.gradeId}
              onChange={(e) =>
                setFormulario({
                  ...formulario,
                  gradeId: e.target.value,
                  sectionId: "",
                })
              }
            >
              <option value="">Sin asignar todavía</option>
              {grados.map((grado) => (
                <option key={grado.id} value={grado.id}>
                  {grado.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Sección
            <select
              value={formulario.sectionId}
              onChange={(e) =>
                setFormulario({ ...formulario, sectionId: e.target.value })
              }
              disabled={formulario.gradeId === ""}
            >
              <option value="">Sin asignar todavía</option>
              {secciones.map((seccion) => (
                <option key={seccion.id} value={seccion.id}>
                  {seccion.name}
                </option>
              ))}
            </select>
          </label>
        </>
      )}
    </div>
  );

  /**
   * Con un formulario abierto la pantalla es SOLO ese formulario.
   *
   * Antes el de edición aparecía ARRIBA del listado, que seguía ahí debajo: había
   * que subir a buscarlo y el contexto de la fila que se estaba editando se perdía.
   * Ahora entra y sale como una vista propia, con su vuelta explícita al listado.
   */
  const enFormulario = mostrarAlta || editando !== null;

  const volverAlListado = (): void => {
    setMostrarAlta(false);
    setEditando(null);
    setHeredados(null);
    setError(null);
  };

  return (
    <section aria-labelledby="titulo-gestion">
      {enFormulario ? (
        <button
          type="button"
          className="boton volver"
          onClick={volverAlListado}
        >
          ← Volver al listado
        </button>
      ) : null}
      <h1 id="titulo-gestion">
        {editando !== null
          ? `Editar — ${editando.fullName}`
          : mostrarAlta
            ? "Nuevo estudiante"
            : "Gestión de estudiantes"}
      </h1>

      {props.conexion !== "EN_LINEA" && (
        <div className="aviso-caja" role="status">
          <strong>Sin conexión.</strong> Puede dar de alta estudiantes: se
          guardan en este dispositivo y se envían al reconectar. El código se
          comprueba contra los datos que ya tiene descargados; si otra caja usó
          el mismo, quedará en revisión al sincronizar. La edición y la baja
          necesitan conexión.
        </div>
      )}

      {aviso !== null && (
        <div className="aviso-caja ok" role="status">
          {aviso}
        </div>
      )}
      {error !== null && (
        <div className="aviso-caja error" role="alert">
          {error}
        </div>
      )}

      {/* --- Alta --------------------------------------------------------- */}
      {!mostrarAlta && editando === null && (
        <button
          type="button"
          className="boton primario"
          onClick={() => abrirAlta()}
        >
          Nuevo estudiante
        </button>
      )}

      {mostrarAlta && (
        <form className="tarjeta" onSubmit={(evento) => void crear(evento)}>
          <label>
            Ciclo escolar
            <select
              value={cicloElegido}
              onChange={(evento) => {
                setCicloElegido(evento.target.value);
                setHeredados(null);
              }}
            >
              {ciclos
                .filter((ciclo) => !ciclo.closed)
                .map((ciclo) => (
                  <option key={ciclo.id} value={ciclo.id}>
                    {ciclo.name}
                  </option>
                ))}
            </select>
          </label>

          {campos(true)}

          {/* Lo que heredará: es la información que evita la sorpresa de que un
              estudiante nuevo aparezca debiendo desde el primer día. */}
          {heredados !== null && (
            <div className="vista-previa">
              <h3>Cargos que heredará del ciclo</h3>
              {heredados.charges.length === 0 ? (
                <p className="vacio">{heredados.notice}</p>
              ) : (
                <>
                  <table className="tabla">
                    <thead>
                      <tr>
                        <th scope="col">Concepto</th>
                        <th scope="col">Vence</th>
                        <th scope="col" className="numero">
                          Monto
                        </th>
                        <th scope="col" className="numero">
                          Mora acumulada
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {heredados.charges.map((cargo, indice) => (
                        <tr key={`${cargo.batchId}-${cargo.month ?? indice}`}>
                          <td>
                            {cargo.monthLabel
                              ? `${cargo.conceptName} — ${cargo.monthLabel}`
                              : cargo.conceptName}
                          </td>
                          <td>{cargo.dueDate ?? "—"}</td>
                          <td className="numero">
                            {formatMoney(
                              money(cargo.amountMinor, heredados.currency),
                            )}
                          </td>
                          <td className="numero">
                            {cargo.lateFeeMinor === 0
                              ? "—"
                              : formatMoney(
                                  money(cargo.lateFeeMinor, heredados.currency),
                                )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th scope="row" colSpan={2}>
                          Total
                        </th>
                        <td className="numero">
                          <strong>
                            {formatMoney(
                              money(
                                heredados.totalCapitalMinor,
                                heredados.currency,
                              ),
                            )}
                          </strong>
                        </td>
                        <td className="numero">
                          <strong>
                            {formatMoney(
                              money(
                                heredados.totalLateFeeMinor,
                                heredados.currency,
                              ),
                            )}
                          </strong>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                  <div className="aviso-caja" role="status">
                    {heredados.notice}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="acciones">
            <button
              type="submit"
              className="boton primario"
              disabled={guardando || cicloElegido === ""}
            >
              {guardando ? "Guardando…" : "Crear estudiante"}
            </button>
            <button type="button" onClick={volverAlListado}>
              Cancelar
            </button>
          </div>
        </form>
      )}

      {/* --- Edición ------------------------------------------------------ */}
      {editando !== null && (
        <form
          className="tarjeta"
          onSubmit={(evento) => void guardarEdicion(evento)}
        >
          {campos(true)}
          <div className="acciones">
            <button
              type="submit"
              className="boton primario"
              disabled={guardando}
            >
              {guardando ? "Guardando…" : "Guardar cambios"}
            </button>
            <button type="button" onClick={volverAlListado}>
              Cancelar
            </button>
          </div>
        </form>
      )}

      {/* --- Listado -------------------------------------------------------
          Desaparece mientras hay un formulario abierto: la pantalla es una cosa
          o la otra, nunca las dos apiladas. */}
      {!enFormulario && (
        <>
          <div className="filtros">
            <label>
              Buscar
              <input
                type="search"
                value={busqueda}
                onChange={(evento) => setBusqueda(evento.target.value)}
                placeholder="Código, nombre, responsable o su teléfono"
              />
            </label>
            <label>
              Estado
              <select
                value={filtro}
                onChange={(evento) => setFiltro(evento.target.value as Filtro)}
              >
                <option value="ACTIVOS">Activos</option>
                <option value="INACTIVOS">Inactivos</option>
                <option value="TODOS">Todos</option>
              </select>
            </label>
          </div>

          {cargando && <p role="status">Cargando estudiantes…</p>}

          <div className="tabla-desplazable">
            <table className="tabla">
              <caption className="oculto-visual">
                Estudiantes de la institución
              </caption>
              <thead>
                <tr>
                  <th scope="col">Código</th>
                  <th scope="col">Estudiante</th>
                  <th scope="col">Responsable</th>
                  <th scope="col">Asignación vigente</th>
                  <th scope="col">Estado</th>
                  <th scope="col">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {estudiantes.map((estudiante) => {
                  const vigente = estudiante.assignments.find(
                    (a) => a.status === "VIGENTE",
                  );
                  return (
                    <tr key={estudiante.id}>
                      <th scope="row">{estudiante.code}</th>
                      <td>{estudiante.fullName}</td>
                      <td>
                        {estudiante.guardianName ?? "—"}
                        {estudiante.guardianPhone && (
                          <span className="ayuda">
                            {" "}
                            · {estudiante.guardianPhone}
                          </span>
                        )}
                      </td>
                      <td>
                        {vigente === undefined || vigente.gradeName === null
                          ? "Pendiente de asignación"
                          : `${vigente.cycleName} · ${vigente.gradeName} ${vigente.sectionName ?? ""}`}
                      </td>
                      <td>
                        {/* Pastilla con símbolo + texto: el color acompaña, no sustituye. */}
                        <span
                          className={`marca-estado ${estudiante.active ? "ok" : "neutro"}`}
                        >
                          <span aria-hidden="true">
                            {estudiante.active ? "✓" : "✕"}
                          </span>
                          {estudiante.active ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                      <td className="acciones">
                        <button
                          type="button"
                          className="boton compacto"
                          onClick={() => abrirEdicion(estudiante)}
                          disabled={props.conexion !== "EN_LINEA"}
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          className={`boton compacto ${estudiante.active ? "peligro" : ""}`}
                          onClick={() => void cambiarEstado(estudiante)}
                          disabled={props.conexion !== "EN_LINEA"}
                        >
                          {estudiante.active ? "Dar de baja" : "Reactivar"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {estudiantes.length === 0 && !cargando && (
                  <tr>
                    <td colSpan={6}>No hay estudiantes con ese filtro.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
